import { useEffect, useMemo, useRef, useState } from "react";
import { getInstituciones } from "services/Reports";
import { Table, Button, Modal, Input, Popover, Tag } from "antd";
import { FaFilePdf } from "react-icons/fa";
import ExportExcel from "components/excel/ExportExcel";
import Loader from "components/loader";
import Card from "components/card";
import DonutChart from "components/charts/DonutChart";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import PickApplyLogo from "assets/img/admin.png";
import {
  generateColor,
  countByBucket,
  ANTIGUEDAD_BUCKETS,
  ACTUALIZACION_BUCKETS,
  getTimeBucket,
  getSearchColumnProps,
  toSafeFileName,
} from "utils/reportHelpers";

const PROVINCIA_DISPLAY_LIMIT = 10;
const PROVINCIA_PDF_DISPLAY_LIMIT = 4;

// Rangos de cantidad de publicaciones — específico de este reporte.
const PUBLICACIONES_BUCKETS = [
  { value: "0", text: "Sin publicaciones", min: 0, max: 0 },
  { value: "1-5", text: "1 a 5", min: 1, max: 5 },
  { value: "6-15", text: "6 a 15", min: 6, max: 15 },
  { value: "16-30", text: "16 a 30", min: 16, max: 30 },
  { value: "31+", text: "31 o más", min: 31, max: Infinity },
];

const getPublicacionesBucket = (count) => {
  if (count == null) return null;
  return PUBLICACIONES_BUCKETS.find((b) => count >= b.min && count <= b.max)?.value || null;
};

const DEFAULT_COLUMN_FILTERS = {
  username: [],
  email: [],
  nombre: [],
  localidad: [],
  plan: [],
  provincia: [],
  activas: [],
  publicaciones: [],
  creado: [],
  actualizado: [],
};

const InstitutionsReports = () => {
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState(DEFAULT_COLUMN_FILTERS);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [reportName, setReportName] = useState("Reporte de instituciones");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfRef = useRef(null);

  const fetchInstituciones = async () => {
    try {
      setIsLoading(true);
      const data = await getInstituciones();
      setIsLoading(false);

      if (data) {
        setAllData(data[0] || []);
      }
    } catch (error) {
      setIsLoading(false);
      return error;
    }
  };

  useEffect(() => {
    fetchInstituciones();
  }, []);

  const provinciaOptions = useMemo(() => {
    const values = new Set(allData.map((item) => item.provincia).filter(Boolean));
    return Array.from(values).sort().map((v) => ({ text: v, value: v }));
  }, [allData]);

  const filteredData = useMemo(() => {
    return allData.filter((entry) => {
      if (columnFilters.username.length > 0 && !entry.user?.name?.toLowerCase().includes(columnFilters.username[0].toLowerCase())) return false;
      if (columnFilters.email.length > 0 && !entry.user?.email?.toLowerCase().includes(columnFilters.email[0].toLowerCase())) return false;
      if (columnFilters.nombre.length > 0 && !entry.nombre?.toLowerCase().includes(columnFilters.nombre[0].toLowerCase())) return false;
      if (columnFilters.localidad.length > 0 && !entry.localidad?.toLowerCase().includes(columnFilters.localidad[0].toLowerCase())) return false;

      if (columnFilters.plan.length > 0) {
        const plan = entry.user?.plan?.detalle?.nombre || "Standard";
        if (!columnFilters.plan.includes(plan)) return false;
      }

      if (columnFilters.provincia.length > 0 && !columnFilters.provincia.includes(entry.provincia)) return false;

      if (columnFilters.activas.length > 0) {
        const tiene = entry.publicaciones_activas_count ? 1 : 0;
        if (!columnFilters.activas.includes(tiene)) return false;
      }

      if (columnFilters.publicaciones.length > 0) {
        const bucket = getPublicacionesBucket(entry.publicaciones_count);
        if (!bucket || !columnFilters.publicaciones.includes(bucket)) return false;
      }

      if (columnFilters.creado.length > 0) {
        const bucket = getTimeBucket(entry.created_at, ANTIGUEDAD_BUCKETS, "month");
        if (!bucket || !columnFilters.creado.includes(bucket)) return false;
      }

      if (columnFilters.actualizado.length > 0) {
        const bucket = getTimeBucket(entry.updated_at, ACTUALIZACION_BUCKETS, "day");
        if (!bucket || !columnFilters.actualizado.includes(bucket)) return false;
      }

      return true;
    });
  }, [allData, columnFilters]);

  const hasActiveFilters = Object.values(columnFilters).some((arr) => arr.length > 0);

  const buildExcelRows = (data) =>
    data.map((item) => ({
      User_ID: item.user_id,
      Username: item.user?.name,
      Email: item.user?.email,
      Nombre: item.nombre,
      Plan: item.user?.plan?.detalle?.nombre || "Standard",
      Ciudad: item.localidad,
      Provincia: item.provincia,
      "Antiguedad en la plataforma": item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY") : "",
      "Última actualización": item.updated_at ? dayjs(item.updated_at).format("DD/MM/YYYY") : "",
      Publicaciones: item.publicaciones_count,
      "¿Tiene publicaciones activas?": item.publicaciones_activas_count ? "Sí" : "No",
      "¿Qué publicó?": item.especialidades_list,
      Postulantes: item.postulaciones_count,
    }));

  const dataExcelFiltered = useMemo(() => buildExcelRows(filteredData), [filteredData]);
  const dataExcelAll = useMemo(() => buildExcelRows(allData), [allData]);

  // Todos los gráficos se calculan sobre filteredData: el 100% siempre es la
  // cantidad de instituciones que dice el contador de arriba.
  const totalFiltrado = filteredData.length;

  const planDistribution = useMemo(() => {
    const counts = new Map();
    filteredData.forEach((item) => {
      const label = item.user?.plan?.detalle?.nombre || "Standard";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData]);

  const activasDistribution = useMemo(() => {
    const counts = new Map([["Sí", 0], ["No", 0]]);
    filteredData.forEach((item) => {
      const label = item.publicaciones_activas_count ? "Sí" : "No";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
  }, [filteredData]);

  const provinciaDistribution = useMemo(() => {
    const counts = new Map();
    filteredData.forEach((item) => {
      const label = item.provincia || "Sin dato";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData]);

  const maxProvinciaCount = provinciaDistribution[0]?.count || 1;
  const provinciaDistributionTop = provinciaDistribution.slice(0, PROVINCIA_DISPLAY_LIMIT);
  const provinciaExtraCount = provinciaDistribution.length - provinciaDistributionTop.length;
  const provinciaDistributionPdfTop = provinciaDistribution.slice(0, PROVINCIA_PDF_DISPLAY_LIMIT);
  const provinciaPdfExtraCount = provinciaDistribution.length - provinciaDistributionPdfTop.length;

  const antiguedadDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getTimeBucket(item.created_at, ANTIGUEDAD_BUCKETS, "month"), ANTIGUEDAD_BUCKETS),
    [filteredData]
  );

  const actualizacionDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getTimeBucket(item.updated_at, ACTUALIZACION_BUCKETS, "day"), ACTUALIZACION_BUCKETS),
    [filteredData]
  );

  const publicacionesDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getPublicacionesBucket(item.publicaciones_count), PUBLICACIONES_BUCKETS),
    [filteredData]
  );

  const activasSiCount = activasDistribution.find((d) => d.label === "Sí")?.count || 0;
  const activasNoCount = activasDistribution.find((d) => d.label === "No")?.count || 0;
  const pctActivas = totalFiltrado > 0 ? Math.round((activasSiCount / totalFiltrado) * 1000) / 10 : 0;

  const planChartOptions = useMemo(
    () => ({
      chart: { type: "donut" },
      labels: planDistribution.map((d) => d.label),
      colors: planDistribution.map((_, i) => generateColor(i, planDistribution.length)),
      legend: { show: true, position: "bottom" },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [planDistribution]
  );

  const antiguedadChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: antiguedadDistribution.map((d) => d.label) },
      colors: antiguedadDistribution.map((_, i) => generateColor(i, antiguedadDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [antiguedadDistribution]
  );

  const actualizacionChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: actualizacionDistribution.map((d) => d.label) },
      colors: actualizacionDistribution.map((_, i) => generateColor(i, actualizacionDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [actualizacionDistribution]
  );

  const publicacionesChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: publicacionesDistribution.map((d) => d.label) },
      colors: publicacionesDistribution.map((_, i) => generateColor(i, publicacionesDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [publicacionesDistribution]
  );

  // Captura la vista oculta armada para el PDF y la parte en tantas páginas A4 como
  // haga falta (mismo criterio que Demografía y Postulaciones).
  const handleDownloadPdf = async () => {
    if (!pdfRef.current) return;

    setIsGeneratingPdf(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const marginMm = 6;
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const usableWidthMm = pageWidthMm - marginMm * 2;
      const usableHeightMm = pageHeightMm - marginMm * 2;

      const imgData = canvas.toDataURL("image/png");
      const imgHeightMm = (canvas.height * usableWidthMm) / canvas.width;

      let heightLeftMm = imgHeightMm;
      let renderedHeightMm = 0;

      pdf.addImage(imgData, "PNG", marginMm, marginMm, usableWidthMm, imgHeightMm);
      heightLeftMm -= usableHeightMm;

      while (heightLeftMm > 0) {
        renderedHeightMm += usableHeightMm;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", marginMm, marginMm - renderedHeightMm, usableWidthMm, imgHeightMm);
        heightLeftMm -= usableHeightMm;
      }

      const safeName = toSafeFileName(reportName, "Reporte de instituciones");
      pdf.save(`${safeName}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`);
      setIsNameModalOpen(false);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const columns = [
    { title: "User_ID", dataIndex: "user_id" },
    {
      title: "Username",
      dataIndex: "username",
      render: (text, record) => record.user?.name,
      ...getSearchColumnProps(columnFilters.username, "Buscar username"),
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text, record) => record.user?.email,
      ...getSearchColumnProps(columnFilters.email, "Buscar email"),
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      ...getSearchColumnProps(columnFilters.nombre, "Buscar nombre"),
    },
    {
      title: "Plan",
      dataIndex: "plan",
      render: (text, record) => record.user?.plan?.detalle?.nombre || "Standard",
      filters: [
        { text: "Gold", value: "Gold" },
        { text: "Standard", value: "Standard" },
      ],
      filteredValue: columnFilters.plan,
    },
    {
      title: "Ciudad",
      dataIndex: "localidad",
      ...getSearchColumnProps(columnFilters.localidad, "Buscar ciudad"),
    },
    {
      title: "Provincia",
      dataIndex: "provincia",
      filters: provinciaOptions,
      filteredValue: columnFilters.provincia,
      filterSearch: true,
    },
    {
      title: "Antiguedad",
      dataIndex: "created_at",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      filters: ANTIGUEDAD_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.creado,
    },
    {
      title: "Last update",
      dataIndex: "updated_at",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
      sorter: (a, b) => dayjs(a.updated_at).unix() - dayjs(b.updated_at).unix(),
      filters: ACTUALIZACION_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.actualizado,
    },
    {
      title: "Publicaciones",
      dataIndex: "publicaciones_count",
      sorter: (a, b) => a.publicaciones_count - b.publicaciones_count,
      filters: PUBLICACIONES_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.publicaciones,
    },
    {
      title: "¿Activas?",
      dataIndex: "publicaciones_activas_count",
      render: (text, record) => (record.publicaciones_activas_count ? "Sí" : "No"),
      filters: [
        { text: "Sí", value: 1 },
        { text: "No", value: 0 },
      ],
      filteredValue: columnFilters.activas,
    },
    {
      title: "Busca...",
      dataIndex: "especialidades_list",
      render: (text) => {
        if (!text) return "—";
        const items = text
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        if (items.length === 0) return "—";

        return (
          <Popover
            title="Especialidades publicadas"
            trigger="click"
            content={
              <div className="flex max-w-xs flex-wrap gap-1">
                {items.map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </div>
            }
          >
            <Button size="small" type="link" className="!px-0">
              {items.length} especialidad{items.length === 1 ? "" : "es"}
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "Postulantes",
      dataIndex: "postulaciones_count",
      sorter: (a, b) => a.postulaciones_count - b.postulaciones_count,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-medium">/ Instituciones</h1>

      <div className="flex flex-row items-center justify-between">
        <span className="text-sm font-semibold">{filteredData.length} instituciones encontradas</span>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <ExportExcel data={dataExcelFiltered} fileName={"Instituciones_resultados_" + new Date().toLocaleDateString()} title="Exportar resultados" />
          )}
          <ExportExcel data={dataExcelAll} fileName={"Instituciones_" + new Date().toLocaleDateString()} title="Exportar todo" />
          {totalFiltrado > 0 && (
            <Button
              icon={<FaFilePdf />}
              onClick={() => setIsNameModalOpen(true)}
              className="flex items-center gap-2 !bg-red-500 !text-white hover:!bg-red-600"
            >
              Descargar PDF
            </Button>
          )}
        </div>
      </div>

      <Modal
        title="Nombre del reporte"
        open={isNameModalOpen}
        onOk={handleDownloadPdf}
        onCancel={() => setIsNameModalOpen(false)}
        okText="Generar PDF"
        cancelText="Cancelar"
        confirmLoading={isGeneratingPdf}
      >
        <p className="mb-2 text-sm text-gray-500">
          Como acá se puede filtrar de muchas formas distintas, ponele un nombre que identifique qué es este reporte.
        </p>
        <Input
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          placeholder="Ej: Instituciones Gold de Buenos Aires"
        />
      </Modal>

      {isLoading ? (
        <Loader />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => record.user_id}
          size="small"
          locale={{ emptyText: "No hay resultados" }}
          onChange={(pagination, tableFilters, sorter, extra) => {
            if (extra.action === "filter") {
              setColumnFilters({
                username: tableFilters.username || [],
                email: tableFilters.email || [],
                nombre: tableFilters.nombre || [],
                localidad: tableFilters.localidad || [],
                plan: tableFilters.plan || [],
                provincia: tableFilters.provincia || [],
                activas: tableFilters.publicaciones_activas_count || [],
                publicaciones: tableFilters.publicaciones_count || [],
                creado: tableFilters.created_at || [],
                actualizado: tableFilters.updated_at || [],
              });
            }
          }}
        />
      )}

      {!isLoading && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 items-start">
            <Card extra="w-full h-[380px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Plan</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} instituciones</p>
              {totalFiltrado > 0 ? (
                <div className="flex h-[300px] w-full items-center justify-center">
                  <DonutChart options={planChartOptions} series={planDistribution.map((d) => d.count)} />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>

            <Card extra="w-full h-[380px] px-6 py-4 lg:col-span-2">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Publicaciones y status</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} instituciones</p>
              {totalFiltrado > 0 ? (
                <div className="flex h-[300px] w-full gap-4">
                  <div className="flex w-[150px] shrink-0 flex-col items-center justify-center gap-2 rounded-xl border border-gray-100 p-3 dark:border-navy-700">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">¿Activas?</span>
                    <span className="text-3xl font-extrabold text-navy-700 dark:text-white">{pctActivas}%</span>
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                      <div className="h-full" style={{ width: `${pctActivas}%`, backgroundColor: "#22c55e" }} />
                      <div className="h-full" style={{ width: `${100 - pctActivas}%`, backgroundColor: "#d1d5db" }} />
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      Sí {activasSiCount} · No {activasNoCount}
                    </span>
                  </div>
                  <div className="flex-1">
                    <Chart
                      options={publicacionesChartOptions}
                      series={[{ name: "Instituciones", data: publicacionesDistribution.map((d) => d.count) }]}
                      type="bar"
                      width="100%"
                      height="100%"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>
          </div>

          <Card extra="w-full px-6 py-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">Provincia</div>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} instituciones</p>
            {provinciaDistributionTop.length > 0 ? (
              <div className="flex flex-col gap-2">
                {provinciaDistributionTop.map((row, idx) => {
                  const pct = totalFiltrado > 0 ? Math.round((row.count / totalFiltrado) * 1000) / 10 : 0;
                  const color = generateColor(idx, provinciaDistributionTop.length);

                  return (
                    <div key={row.label} className="flex items-center gap-3">
                      <span className="w-40 shrink-0 truncate text-sm font-medium text-navy-700 dark:text-white" title={row.label}>
                        {row.label}
                      </span>
                      <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(row.count / maxProvinciaCount) * 100}%`, backgroundColor: color }}
                        />
                      </div>
                      <span className="w-24 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                        {row.count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
                {provinciaExtraCount > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">+ {provinciaExtraCount} provincias más</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 items-start">
            <Card extra="w-full h-[420px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Antigüedad</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} instituciones</p>
              {totalFiltrado > 0 ? (
                <div className="h-[340px] w-full">
                  <Chart
                    options={antiguedadChartOptions}
                    series={[{ name: "Instituciones", data: antiguedadDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>

            <Card extra="w-full h-[420px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Última actualización</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} instituciones</p>
              {totalFiltrado > 0 ? (
                <div className="h-[340px] w-full">
                  <Chart
                    options={actualizacionChartOptions}
                    series={[{ name: "Instituciones", data: actualizacionDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Layout exclusivo para el PDF: mismo criterio que Demografía y Postulaciones. */}
      <div
        ref={pdfRef}
        aria-hidden="true"
        className="pointer-events-none fixed -left-[9999px] top-0 flex w-[820px] flex-col gap-5 bg-white p-5"
      >
        <div className="flex items-center justify-between border-b-4 border-main pb-3">
          <img src={PickApplyLogo} alt="PickApply" className="h-16" />
          <span className="text-sm font-semibold text-gray-500">Reporte de instituciones</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[32px] font-extrabold text-navy-700">{reportName || "Reporte de instituciones"}</span>
          <span className="mt-1 text-base text-gray-500">
            {totalFiltrado} instituciones · Generado el {dayjs().format("DD/MM/YYYY HH:mm")}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Plan</span>
            {totalFiltrado > 0 ? (
              <div className="flex h-[240px] w-full items-center justify-center">
                <DonutChart options={planChartOptions} series={planDistribution.map((d) => d.count)} />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>

          <div className="col-span-2 flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Publicaciones y status</span>
            {totalFiltrado > 0 ? (
              <div className="flex gap-4">
                <div className="flex w-[130px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-gray-100 p-2">
                  <span className="text-xs font-semibold uppercase text-gray-500">¿Activas?</span>
                  <span className="text-2xl font-extrabold text-navy-700">{pctActivas}%</span>
                  <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full" style={{ width: `${pctActivas}%`, backgroundColor: "#22c55e" }} />
                    <div className="h-full" style={{ width: `${100 - pctActivas}%`, backgroundColor: "#d1d5db" }} />
                  </div>
                  <span className="text-[10px] text-gray-500">
                    Sí {activasSiCount} · No {activasNoCount}
                  </span>
                </div>
                <div className="h-[220px] flex-1">
                  <Chart
                    options={publicacionesChartOptions}
                    series={[{ name: "Instituciones", data: publicacionesDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
          <span className="text-xl font-bold text-navy-700">Provincia</span>
          {provinciaDistributionPdfTop.length > 0 ? (
            <div className="mt-1 flex flex-col gap-2">
              {provinciaDistributionPdfTop.map((row, idx) => {
                const pct = totalFiltrado > 0 ? Math.round((row.count / totalFiltrado) * 1000) / 10 : 0;
                const color = generateColor(idx, provinciaDistributionPdfTop.length);

                return (
                  <div key={row.label} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate text-base font-medium text-navy-700">{row.label}</span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${(row.count / maxProvinciaCount) * 100}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="w-28 shrink-0 text-right text-sm text-gray-500">
                      {row.count} ({pct}%)
                    </span>
                  </div>
                );
              })}
              {provinciaPdfExtraCount > 0 && (
                <p className="mt-1 text-sm text-gray-500">+ {provinciaPdfExtraCount} provincias más</p>
              )}
            </div>
          ) : (
            <p className="text-base text-gray-500">Sin datos</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Antigüedad</span>
            {totalFiltrado > 0 ? (
              <div className="h-[260px] w-full">
                <Chart
                  options={antiguedadChartOptions}
                  series={[{ name: "Instituciones", data: antiguedadDistribution.map((d) => d.count) }]}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Última actualización</span>
            {totalFiltrado > 0 ? (
              <div className="h-[260px] w-full">
                <Chart
                  options={actualizacionChartOptions}
                  series={[{ name: "Instituciones", data: actualizacionDistribution.map((d) => d.count) }]}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstitutionsReports;
